import { WizardStep, type WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import PersonalInformationForm from 'modules/benefits/components/benefitModals/HealthcareEnrollmentWizard/components/PersonalInformationForm'
import {
  HealthcareEnrollmentData,
  benefitEnrollmentField,
  isMemberTypeValue,
  isSexValue,
} from 'modules/benefits/components/benefitModals/HealthcareEnrollmentWizard/types'
import { personalInformationStepSchema } from 'modules/benefits/components/benefitModals/HealthcareEnrollmentWizard/validation'

interface Props {
  data: HealthcareEnrollmentData
  onDataChanged: (data: HealthcareEnrollmentData) => void
  identifierTypes: Array<{ value: string; label: string }>
}

const PersonalInformationStep: React.FC<Props> = ({ data, onDataChanged, identifierTypes }) => {
  useEffect(() => {
    track(trackingEvents.benefits.advanceToHealthcareEnrollmentPersonalInformationStep)
  }, [])

  // Handle field changes from the form and update the wizard data
  const handleFieldChange = (fieldName: string, value: string) => {
    const typedField = benefitEnrollmentField`${fieldName}`
    const updatedData = { ...data }

    // Handle special cases for fields that need specific typing
    if (typedField === 'sex' && isSexValue(value)) {
      updatedData.personalInformationData[typedField] = value
    } else if (typedField === 'memberType' && isMemberTypeValue(value)) {
      updatedData.personalInformationData[typedField] = value
    } else if (typedField !== 'sex' && typedField !== 'memberType') {
      // For all other string fields, we can safely assign
      updatedData.personalInformationData[typedField] = value
    }

    onDataChanged(updatedData)
  }

  return (
    <PersonalInformationForm
      data={data.personalInformationData}
      identifierTypes={identifierTypes}
      onChange={handleFieldChange}
    />
  )
}

export const personalInformationStep = (): WizardStep<HealthcareEnrollmentData> => ({
  title: i18n.t('benefits.healthcare.enrollment_wizard.personal_information_step.title'),
  nextText: i18n.t('common.save'),
  render: (props: WizardStepRenderParams<HealthcareEnrollmentData>) => (
    <PersonalInformationStep
      data={props.data}
      onDataChanged={props.onDataChanged}
      identifierTypes={props.data.identifierTypes || []}
    />
  ),
  isCompleted: (data: HealthcareEnrollmentData) => {
    // Use the validation schema to check if the step is complete
    const validationResult = personalInformationStepSchema().safeParse(data.personalInformationData)
    return validationResult.success
  },
})
